import { useMachine } from '@xstate/react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLegal } from '../../components/ApplicationProviders/LegalProvider/LegalProvider';
import { useProduct } from '../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { ApplicationEvent, PageView } from '../../models/analytics';
import { SessionModel } from '../../models/session';
import { useSelectedAuthenticationOptions } from '../../payments/hooks/useSelectedAuthenticationOptions/useSelectedAuthenticationOptions';
import { useAppSelector } from '../../stores/redux-store';
import { applicationParameters } from '../../utilities/application-parameters';
import { useConfiguration } from '../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import {
  createCredentialsCreatorMachine,
  CredentialsCreatorMachineContext,
  CredentialsCreatorMachineProps,
} from '../machines/credentialsCreatorMachine/credentialsCreatorMachine';
import { handoffFlowAvailable, ProviderEntity } from '../../models/provider';
import { CredentialsEntity } from '../../models/credentials';
import { getCredentialsAppUri } from '../../utilities/app-uri/app-uri';
import {
  navigateUserToInitialTinkLinkAfterThirdPartyRedirect,
  shouldRedirectToReferPage,
} from '../../models/configuration';
import { setUserCredentials } from '../../stores/user-credentials-store';
import { useApplicationEvent } from '../../core/Analytics/useApplicationEvent';
import { ParentApplicationEvent } from '../../core/Analytics/ParentApplicationEventMessenger';
import { shouldAutoSubmitCredentials } from './machineUtils';

export type UseCredentialsCreatorMachineProps = Pick<
  CredentialsCreatorMachineProps,
  'onCredentialsCreated'
> &
  Pick<
    CredentialsCreatorMachineProps['submitCredentialsPayload'],
    'triggerRefresh' | 'refreshableItems' | 'userCredentials'
  > & {
    providedCredentials?: CredentialsEntity;
    provider: ProviderEntity;
    appUri?: string;
    enableAutoSubmitCredentials?: boolean;
    session?: SessionModel;
    authenticate?: boolean;
    showDefaultFlowLoaders?: boolean; // TODO: remove after A/B Test
  };

export const useCredentialsCreatorMachine = ({
  appUri,
  userCredentials,
  providedCredentials,
  enableAutoSubmitCredentials = true,
  provider,
  session,
  triggerRefresh,
  onCredentialsCreated,
  refreshableItems,
  authenticate = applicationParameters.get('authenticate'),
  showDefaultFlowLoaders = false,
}: UseCredentialsCreatorMachineProps) => {
  const { data } = useConfiguration();
  const { selectedAuthenticationOptions } = useSelectedAuthenticationOptions(provider);
  const isClientVerified = useAppSelector((state) => state.oauth.description?.verified ?? false);
  const callbackUri = applicationParameters.get('callbackUri');
  const { productType, productNames } = useProduct();
  const { hasUserReadLegalDisclaimer } = useLegal();
  const user = useAppSelector((state) => state.user.user);
  const enableHandoff = handoffFlowAvailable(provider, data, user?.id);
  const { sendEvent } = useApplicationEvent();
  const shouldAutoSubmit = shouldAutoSubmitCredentials({
    provider,
    configuration: data,
    productType,
    personalIdentifier: session?.personalIdentifier,
    userHasReadDisclaimer: hasUserReadLegalDisclaimer(PageView.SUBMIT_CREDENTIALS_SCREEN),
    isClientVerified,
    enableAutoSubmitCredentials,
  });
  const sendOnAuthErrorAnalytics = () => {
    sendEvent(
      ApplicationEvent.CREDENTIALS_VALIDATION_FAILED,
      ParentApplicationEvent.CREDENTIALS_VALIDATION_FAILED
    );
  };
  const username = session?.personalIdentifier || applicationParameters.get('username');
  const fields: CredentialsCreatorMachineContext['fields'] = username ? { username } : {};
  const dispatch = useDispatch();

  const [machine] = useState(
    createCredentialsCreatorMachine({
      shouldAutoSubmit,
      submitCredentialsPayload: {
        userCredentials,
        existingCredentials: providedCredentials,
        appUri:
          appUri ||
          getCredentialsAppUri({
            redirectToReferPage: shouldRedirectToReferPage(data),
            navigateToInitialTinkLinkRedirect:
              navigateUserToInitialTinkLinkAfterThirdPartyRedirect(data),
          }),
        productNames,
        providerName: provider.name,
        callbackUri,
        selectedAuthenticationOptions,
        triggerRefresh,
        refreshableItems,
        authenticate,
      },
      onCredentialsCreated: (credentials) => {
        dispatch(setUserCredentials(credentials));
        onCredentialsCreated?.(credentials);
      },
      sendOnAuthErrorAnalytics,
      initialContext: {
        credentials: providedCredentials,
        enableHandoff,
        fields,
        showDefaultFlowLoaders,
      },
    })
  );

  return useMachine(machine);
};
